/**
 * RasterLoader — the mark a *control* wears while its own action is in flight.
 *
 * CLAUDE.md, "Loading states", third case: an action in flight is shown by the control that
 * started it, and the rest of the screen stays usable. It replaces lucide `Loader2`
 * everywhere — a button, a row, an inline sentence — so that the product has one loading
 * hand rather than a spinner here and a section there.
 *
 * Geometry from `design/project/loader/index.html`, owner-approved 2026-09-12. Nine cells,
 * each delayed by `(row + col) × 120ms`, which is what turns nine identical 1.2s loops into
 * one diagonal sweep. The delays are computed rather than written out: nine hand-typed
 * numbers are nine chances for the diagonal to be wrong in one corner.
 *
 * `aria-hidden`. It is never the accessible answer — the control it sits in already carries
 * `aria-busy`, and the page's own `role="status"` says the sentence.
 */

import styles from './RasterLoader.module.css';

const SIDE = 3;
const STEP_MS = 120;

/** Row-major, so the DOM order is the reading order and the grid does the placing. */
const CELLS = Array.from({ length: SIDE * SIDE }, (_, index) => {
  const row = Math.floor(index / SIDE);
  const column = index % SIDE;
  return { key: `${String(row)}-${String(column)}`, delayMs: (row + column) * STEP_MS };
});

export interface RasterLoaderProps {
  /**
   * Which ground it is drawn on. `paper` is the default — a white or light control, where
   * the cells run between `neutral-300` and `neutral-900`. `ink` is a dark one (the primary
   * button, the chrome), where they run between translucent white and white.
   */
  readonly tone?: 'paper' | 'ink';
}

export function RasterLoader({ tone = 'paper' }: RasterLoaderProps) {
  const classes = [styles.raster, tone === 'ink' ? styles.ink : ''].filter(Boolean).join(' ');

  return (
    <span className={classes} aria-hidden>
      {CELLS.map((cell) => (
        <span key={cell.key} className={styles.cell} style={{ animationDelay: `${String(cell.delayMs)}ms` }} />
      ))}
    </span>
  );
}
